/* eslint-disable @typescript-eslint/no-explicit-any */
import { HighlightOffOutlined } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  Grid,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Container } from '@mui/system';
import { useCallback, useEffect, useState } from 'react';
import {
  useSubmitPresetsFileMutation,
  useSyncPresetsMutation,
  useValidateFileMutation,
} from 'src/backend';
import DataField from 'src/components/DataField/DataField';
import LoadingButton from 'src/components/LoadingButton';
import { DropzoneArea } from 'mui-file-dropzone';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { MACHINE_GROUP_DETAILS } from 'src/router/constants';
import { generatePath } from 'src/utils';
import { useNavigate } from 'react-router-dom';
import BulkInsertFileInfo from './BulkInsertFileInfo';
import { ROUTE_MACHINES } from './constants';

export type ValidationResult = {
  inserts: number;
  updates: number;
  total: number;
  warnings: string[];
  errors: string[];
  isValid: boolean;
};

type Params = {
  isOpen: boolean;
  machineGroupId: string;
  machineType: string;
  closeForm: () => void;
};

function UploadPresetsForm({
  isOpen,
  machineGroupId,
  machineType,
  closeForm,
}: Params) {
  const [validateDisabled, setValidateDisabled] = useState(true);
  const [userConfirmed, setUserConfirmed] = useState(false);

  const [file, setFile] = useState<File>(null as unknown as File);
  const [autoSyncPresets, setAutoSyncPresets] = useState(false);

  const [validateFile, { isLoading }] = useValidateFileMutation();
  const submitPresetsFile = useSubmitPresetsFileMutation();

  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null);

  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();
  const syncPresets = useSyncPresetsMutation();

  useEffect(() => {
    if (
      !validationResult ||
      validationResult.isValid === false ||
      (validationResult.inserts === 0 && validationResult.updates === 0)
    ) {
      setAutoSyncPresets(false);
    } else {
      setAutoSyncPresets(true);
    }
  }, [isOpen, validationResult]);

  const setDefaults = () => {
    setFile(null as unknown as File);
    setValidationResult(null);
    setValidateDisabled(true);
    setUserConfirmed(false);
  };

  const handleFileUpload = (file: File) => {
    if (!file) {
      setDefaults();
      return;
    }
    setFile(file);
    setValidateDisabled(false);
  };

  const handleValidate = (e: { preventDefault: () => void }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('machineType', machineType);

    validateFile({ id: machineGroupId, formData })
      .unwrap()
      .then((x) => {
        setValidationResult(x.data);
        setValidateDisabled(true);
      });
  };

  const onClose = useCallback(() => {
    setDefaults();
    closeForm();
  }, [closeForm]);

  const changeTabAndAutoSync = useCallback(() => {
    const startPresetSyncRequest = {
      machineIds: null,
      presetIds: null,
      includeOffline: false,
    };
    syncPresets[0]({ id: machineGroupId, startPresetSyncRequest })
      .unwrap()
      .then(() => {
        const machineGroupMachinesPath = `${generatePath(
          MACHINE_GROUP_DETAILS,
          {
            id: machineGroupId,
          },
        )}/${ROUTE_MACHINES}`;
        navigate(machineGroupMachinesPath);
      })
      .finally(() => {
        onClose();
      });
  }, [syncPresets, machineGroupId, navigate, onClose]);

  const handleSubmitFile = () => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('machineType', machineType);

    submitPresetsFile[0]({ id: machineGroupId, formData })
      .unwrap()
      .finally(() => {
        if (autoSyncPresets) {
          changeTabAndAutoSync();
        } else {
          onClose();
        }
      });
  };

  return (
    <>
      <BulkInsertFileInfo
        showModal={showModal}
        machineType={machineType}
        closeModal={() => setShowModal(false)}
      />
      <Dialog
        open={isOpen}
        onClose={() => onClose()}
        fullWidth
        PaperProps={{
          sx: {
            height: '45em',
          },
        }}
        sx={{
          width: '100%',
          height: '100%',
        }}
      >
        <DialogTitle sx={{ fontSize: '2.5em', padding: '30px' }}>
          Bulk insert presets ({machineType})
          <div style={{ float: 'right' }}>
            <IconButton onClick={() => setShowModal(true)}>
              <InfoOutlinedIcon fontSize="large" />
            </IconButton>
            <IconButton onClick={() => onClose()}>
              <HighlightOffOutlined fontSize="large" />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} padding={3}>
            <Grid item xs={4}>
              <DropzoneArea
                fileObjects={[]}
                acceptedFiles={['.csv']}
                dropzoneText="Drag and drop a file here or click"
                onChange={(files) => handleFileUpload(files[0])}
                showPreviews={false}
                showAlerts={false}
                filesLimit={1}
                maxFileSize={10000000} // 10 MB
              />
              {file && (
                <Container sx={{ marginTop: '10px' }}>
                  <DataField label="Name" value={file.name} />
                  <DataField
                    label="Size"
                    value={`${(file.size / 1024).toFixed(2)} KB`}
                  />
                  <DataField label="Type" value={file.type} />
                </Container>
              )}
            </Grid>
            <Grid item xs={8}>
              <Container sx={{ width: '50%' }}>
                <LoadingButton
                  loading={isLoading}
                  variant="outlined"
                  type="button"
                  disabled={validateDisabled}
                  size="medium"
                  onClick={handleValidate}
                >
                  Validate file
                </LoadingButton>
              </Container>
              <Container
                sx={{
                  marginTop: '10px',
                  marginLeft: '10px',
                  width: '100%',
                  alignItems: 'center',
                  fontSize: 'medium',
                }}
              >
                {validationResult && (
                  <>
                    {validationResult.isValid ? (
                      <span style={{ color: 'green' }}>
                        Successfully validated
                      </span>
                    ) : (
                      <span style={{ color: 'red' }}>Validation failed</span>
                    )}
                    <br />
                    <div>Presets to be added: {validationResult.inserts}</div>
                    <div>Presets to be updated: {validationResult.updates}</div>
                    <div>All presets count: {validationResult.total}</div>
                    <br />
                    {validationResult.warnings.map((warning) => (
                      <div key={warning} style={{ color: 'orange' }}>
                        {warning}
                      </div>
                    ))}
                    {validationResult.errors.map((error) => (
                      <div key={error} style={{ color: 'red' }}>
                        {error}
                      </div>
                    ))}
                    <br />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={userConfirmed}
                          disabled={validationResult.isValid === false}
                          onChange={(e) => {
                            setUserConfirmed(e.target.checked);
                          }}
                        />
                      }
                      label="I confirm the details of the import"
                    />
                    <br />
                    <LoadingButton
                      loading={
                        submitPresetsFile[1].isLoading ||
                        syncPresets[1].isLoading
                      }
                      variant="outlined"
                      type="button"
                      disabled={
                        validationResult.isValid === false || !userConfirmed
                      }
                      size="medium"
                      onClick={handleSubmitFile}
                    >
                      Upload presets
                    </LoadingButton>
                  </>
                )}
              </Container>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default UploadPresetsForm;
